import { useState } from 'react'
import type { ReactNode } from 'react'

import type { Operation } from '@fit-editor/fit-core'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { OperationPreviewSummary } from '@/components/edit/operation-preview-summary'
import { RangeFields } from '@/components/edit/range-fields'
import { useOperationPreview } from '@/hooks/use-operation-preview'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface RangeOperationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  idPrefix: string
  title: string
  description: string
  confirmLabel: string
  buildOperation: (start: Date, end: Date) => Operation
  /** Extra fields rendered between the range inputs and the preview (e.g. `setGpsConstant`'s lat/lon). */
  extraFields?: ReactNode
  /** When `extraFields` is present and its own input is invalid, disables Confirm even if the range itself is valid. */
  extraValid?: boolean
}

/**
 * The shared range-input-plus-preview dialog, reused as-is by
 * removeInterval / removeGpsOnly / interpolateGps, and by setGpsConstant
 * via its `extraFields` slot. Crop has its own dialog (`CropDialog`) since
 * its before/after/range modes need a different, variable set of
 * required fields -- but it's built from the same `RangeFields` +
 * `useOperationPreview` + `OperationPreviewSummary` pieces as this one.
 */
export function RangeOperationDialog({
  open,
  onOpenChange,
  idPrefix,
  title,
  description,
  confirmLabel,
  buildOperation,
  extraFields,
  extraValid = true,
}: RangeOperationDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const selectedRangeMs = useActivityStore((s) => s.selectedRangeMs)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  const [start, setStart] = useState<Date | null>(null)
  const [end, setEnd] = useState<Date | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Prefill from the timeline selection exactly once per open transition
  // -- deliberately done during render (the React-documented pattern for
  // "adjust state when a prop changes", not an effect, since an effect
  // doing this would be a synchronous-setState-in-effect smell) rather
  // than in a `useEffect`. Only prefills when nothing's entered yet, and
  // never resets existing start/end to null -- `setGpsConstant`'s "pick
  // on map" flow closes and reopens this same dialog instance around the
  // map click, and must not lose whatever range the user had already
  // entered. `prevOpen` starts at `false` (not `open`) so this also
  // fires correctly if a dialog is ever first rendered already-open.
  const [prevOpen, setPrevOpen] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open && start == null && end == null && selectedRangeMs != null) {
      setStart(new Date(selectedRangeMs.start))
      setEnd(new Date(selectedRangeMs.end))
    }
  }

  const rangeValid = start != null && end != null && start.getTime() < end.getTime()
  const operation = rangeValid ? buildOperation(start, end) : null

  // Only fetches a baseline/preview while the dialog is actually open --
  // every edit dialog is always mounted (see `EditMenu`'s doc comment),
  // so without this guard every one of them would hit the worker as soon
  // as any activity is selected, not just the one the user opened.
  const preview = useOperationPreview(open ? loaded?.history : undefined, operation)

  if (loaded == null) return null

  const handleConfirm = async () => {
    if (operation == null) return
    setSubmitting(true)
    try {
      // `null` means the apply failed (the store already surfaced a
      // toast) -- keep the dialog open with the user's range intact
      // rather than closing it as if the edit had actually been applied.
      const warnings = await applyOperationToSelected(operation)
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={`${idPrefix}-description`}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription id={`${idPrefix}-description`}>{description}</DialogDescription>

        <div className="flex flex-col gap-3">
          <RangeFields
            idPrefix={idPrefix}
            start={start}
            end={end}
            onStartChange={setStart}
            onEndChange={setEnd}
            selectedRangeMs={selectedRangeMs}
          />
          {!rangeValid && (start != null || end != null) && (
            <p className="text-destructive text-xs">
              The start must be before the end, and both must be set.
            </p>
          )}
          {extraFields}
          <OperationPreviewSummary state={preview} ready={rangeValid} />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!rangeValid || !extraValid || submitting}
            onClick={() => void handleConfirm()}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
